import { useState } from 'react';
import { Form, TextInput, SubmitButton } from '@components/shared/forms';
import { CurrentUser } from '@root/types';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal, Notification } from '@components/shared';
import * as Yup from 'yup';
import { useFlags } from 'launchdarkly-react-client-sdk';
import s from '../AccountSettings.module.less';
import {
  CompanyLogoDisplay,
  EmailDisplay,
  FirstNameDisplay,
  FormattedTourbookPhoneNumber,
  LastNameDisplay,
  YourPhotoDisplay,
} from '../utils';

type Props = {
  currentUser: CurrentUser;
  handleSubmit: (any) => void;
};

const TourbookInfoForm = ({ currentUser, handleSubmit }: Props) => {
  const { t } = useTranslation('accountSettings');
  const flags = useFlags();
  const usePhoneNumberFormatter = flags['phone-number-format'];

  const [cloudinaryLoading, setCloudinaryLoading] = useState(false);
  const [avatarCloudinaryId, setAvatarCloudinaryId] = useState<string>(
    currentUser.tourbookAvatarCloudinaryId || '',
  );
  const [companyLogoCloudinaryId, setCompanyLogoCloudinaryId] = useState<string>(
    currentUser.tourbookCompanyLogoCloudinaryId || '',
  );
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleCloudinaryFileInputError = (error: string) => {
    Notification.info({
      title: t('notification.updateError'),
      text: error,
      placement: 'bottomLeft',
    });
  };

  const [formValues, setFormValues] = useState({});

  const handleSaveUpdates = values => {
    setIsConfirmModalOpen(true);
    setFormValues({
      ...currentUser,
      ...values,
      tourbookPhoneNumber: values.tourbookPhoneNumber ?? '',
      tourbookAvatarCloudinaryId: avatarCloudinaryId,
      tourbookCompanyLogoCloudinaryId: companyLogoCloudinaryId,
    });
  };

  const handleConfirmSave = () => {
    handleSubmit(formValues);
    setIsConfirmModalOpen(false);
  };

  const validationSchema = Yup.object().shape({
    // Keep phone number simple and include no validation
    tourbookPhoneNumber: Yup.string().nullable(),
  });

  return (
    <>
      <Form
        id="accountTourbookInfoForm"
        onSubmit={handleSaveUpdates}
        layout="vertical"
        data-testid="accountSettingsForm"
        initialValues={{
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          jobTitle: currentUser.jobTitle,
          tourbookPhoneNumber:
            (usePhoneNumberFormatter
              ? currentUser.tourbookPhoneNumber
              : currentUser.formattedTourbookPhoneNumber) ?? '',
          marketSlug: currentUser.marketSlug,
          role: currentUser.role,
        }}
        validationSchema={validationSchema}
        enablePrompt
      >
        <div className={s.photosGrid}>
          <YourPhotoDisplay
            currentUser={currentUser}
            isEditable
            image={currentUser.tourbookAvatarImage}
            setHeaderCloudinaryId={setAvatarCloudinaryId}
            setCloudinaryLoading={setCloudinaryLoading}
            handleCloudinaryFileInputError={handleCloudinaryFileInputError}
          />
          <CompanyLogoDisplay
            currentUser={currentUser}
            isEditable
            image={currentUser.tourbookCompanyImage}
            setHeaderCloudinaryId={setCompanyLogoCloudinaryId}
            setCloudinaryLoading={setCloudinaryLoading}
            handleCloudinaryFileInputError={handleCloudinaryFileInputError}
          />
        </div>
        <div className={s.inputGrid}>
          {usePhoneNumberFormatter ? (
            <>
              <TextInput
                containerClass={s.accountItem}
                labelText={t('phone')}
                name="tourbookPhoneNumber"
              />
              <FormattedTourbookPhoneNumber />
            </>
          ) : (
            <TextInput
              containerClass={s.phoneItem}
              labelText={t('phone')}
              name="tourbookPhoneNumber"
            />
          )}
          <FirstNameDisplay currentUser={currentUser} />
          <LastNameDisplay currentUser={currentUser} />
          <EmailDisplay currentUser={currentUser} />
        </div>
        <div className={s.submitButtonWrapper}>
          <SubmitButton
            data-testid="saveUpdatesButton"
            className={s.submitButton}
            disabled={cloudinaryLoading}
          >
            {t('saveUpdates')}
          </SubmitButton>
        </div>
        <ConfirmationModal
          onConfirm={handleConfirmSave}
          onClose={() => setIsConfirmModalOpen(false)}
          confirmationText={t('tourbookInfoForm.confirmationModal.confirmMessage')}
          title={t('tourbookInfoForm.confirmationModal.title')}
          isOpen={isConfirmModalOpen}
          cancelButtonText={t('common:modelList.confirmation.cancelText')}
          confirmButtonText={t('tourbookInfoForm.confirmationModal.confirmButtonText')}
        />
      </Form>
    </>
  );
};

export default TourbookInfoForm;
